<template>
  <v-card v-if="currentDocument" class="observer-card pe-3">
    <div class="d-flex flex-row rounded-0 align-center ga-3">
      <v-icon color="primary" size="medium"> fas fa-eye </v-icon>
      <div class="flex-column w-100">
        <div class="observer-title">{{ i18n.t('titles.observer_view') }}</div>
        <div v-if="!isDocumentAuthRoute" class="observer-email">
          {{ userEmail }}
        </div>
        <slot></slot>
      </div>

      <div class="flex-column align-center align-self-start">
        <DocumentMenu
          ref="documentMenu"
          :document="currentDocument"
          :policy="filteredPolicies"
          :sign-key="signKey"
          target="parent"
        >
          <template #activator>
            <v-btn
              class="bg-transparent"
              data-test-id="signing-card-options"
              icon
              @click="openDocumentMenu"
            >
              <v-icon class="font-weight-regular">
                fa fa-ellipsis-vertical
              </v-icon>
            </v-btn>
          </template>
        </DocumentMenu>
      </div>
    </div>
    <DocumentDialog />
  </v-card>
</template>

<script setup lang="ts">
import DocumentMenu from '@/components/menus/DocumentMenu.vue';
import DocumentDialog from '@/components/dialogs/document-dialog/DocumentDialog.vue';
import { useI18n } from 'vue-i18n';
import { useSignContext } from '@/composables/useSignContext';
import { computed, ref } from 'vue';
import { useDocumentContext } from '@/composables/useDocumentContext';
import { constants } from '@/constants/constants';
import { DocumentStatus } from '@/types/enums/DocumentStatus.ts';

const { userEmail, policy, signKey } = useSignContext();
const { currentDocument, isDocumentAuthRoute } = useDocumentContext();
const i18n = useI18n();
const documentMenu = ref();

const filteredPolicies = computed(() => {
  const activePolicy =
    policy.value && Object.keys(policy.value).length > 0
      ? policy.value
      : currentDocument.value?.documentPolicy || {};

  if (
    activePolicy &&
    currentDocument.value?.documentStatus === DocumentStatus.WITHDRAWN
  ) {
    return Object.keys(activePolicy)
      .filter((key) => !constants.SEAL_POLICIES_TO_OMIT.includes(key))
      .reduce((acc: Record<string, unknown>, key) => {
        acc[key] = activePolicy[key];
        return acc;
      }, {});
  }

  return activePolicy;
});

function openDocumentMenu() {
  documentMenu.value.openMenu();
}
</script>

<style scoped lang="scss">
.observer-card {
  box-sizing: border-box;
  border-radius: 7px;
  background-color: rgb(var(--v-theme-grey-lighten-5));
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid rgb(var(--v-theme-grey-lighten-2));
  margin-bottom: 32px;
  margin-top: 16px;
  padding: 16px;
}

.observer-title {
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: black;
}

.observer-email {
  color: rgb(var(--v-theme-grey-darken-1));
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
}
</style>
